import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuthStore } from '@store'
import apiClient from '@services/api/client'

function PaymentPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  const { postData, postId, isEdit, postType, initialDuration } = location.state || {}
  
  const [selectedMethod, setSelectedMethod] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [isFreeTrialActive, setIsFreeTrialActive] = useState(false)
  const [freeTrialDaysLeft, setFreeTrialDaysLeft] = useState(0)
  const [duration, setDuration] = useState(initialDuration || 7) // Durée en jours (7, 15, 30)
  const [imageCount, setImageCount] = useState(postData?.images?.length || 1)

  // Tarification pour les articles/produits
  const PRICING_ARTICLES = {
    3: 500,    // 3 jours - 500 FCFA
    7: 1000,   // 7 jours - 1000 FCFA (recommandé)
    14: 1800,  // 14 jours - 1800 FCFA (économie de 10%)
    30: 3500   // 30 jours - 3500 FCFA (économie de 20%)
  }
  
  // Tarification pour les vidéos et bannières
  const PRICING_VIDEO_BANNER = {
    3: 1500,   // 3 jours - 1500 FCFA
    7: 3000,   // 7 jours - 3000 FCFA (recommandé)
    14: 5500,  // 14 jours - 5500 FCFA (économie de 8%)
    30: 10000  // 30 jours - 10000 FCFA (économie de 17%)
  }
  
  const PRICE_PER_IMAGE = 100 // Prix par image supplémentaire après la première

  // Choisir la grille tarifaire selon le type de contenu
  const getPricing = () => {
    if (postType === 'video' || postType === 'banner') {
      return PRICING_VIDEO_BANNER
    }
    return PRICING_ARTICLES
  }

  // Calcul du prix total
  const calculatePrice = () => {
    const pricing = getPricing()
    // Prix de base selon la durée choisie
    let price = pricing[duration] || pricing[7]
    
    // Ajout du coût selon le nombre d'images (première image incluse dans le prix de base)
    // Les images supplémentaires ne s'appliquent qu'aux articles
    if (postType !== 'video' && postType !== 'banner' && imageCount > 1) {
      price += (imageCount - 1) * PRICE_PER_IMAGE
    }
    
    return price
  }

  const totalPrice = calculatePrice()

  useEffect(() => {
    // Vérifier si l'utilisateur est en période d'essai gratuite
    checkFreeTrial()
  }, [user])

  const checkFreeTrial = async () => {
    try {
      const r = await apiClient.get('/subscription/status')
      if (r.isFreeTrialActive) {
        setIsFreeTrialActive(true)
        setFreeTrialDaysLeft(r.daysLeft)
      }
    } catch {
      // default false
    }
  }

  const handleFreeTrial = async () => {
    setIsProcessing(true)

    try {
      // Mettre à jour le post avec la durée par défaut (7 jours) pour la période gratuite
      if (postId) {
        try {
          await apiClient.put(`/posts/${postId}`, { duration: 7, payment_status: 'free_trial' })
        } catch (err) {
          console.error('Post update error:', err)
        }
      }

      // Publier directement sans paiement pendant la période gratuite
      toast.success('Publication gratuite activée !')
      
      setTimeout(() => {
        navigate('/dashboard', { 
          state: { 
            paymentSuccess: true,
            message: isEdit ? 'Annonce mise à jour avec succès !' : 'Annonce publiée avec succès !',
            isFreeTrial: true
          } 
        })
      }, 1500)

    } catch (error) {
      console.error('Erreur:', error)
      toast.error('Erreur lors de la publication. Veuillez réessayer.')
    } finally {
      setIsProcessing(false)
    }
  }

  const paymentMethods = [
    {
      id: 'wave',
      name: 'Wave',
      icon: '🌊',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600'
    },
    {
      id: 'orange',
      name: 'Orange Money',
      icon: '🍊',
      color: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600'
    },
    {
      id: 'mtn',
      name: 'MTN Money',
      icon: '📱',
      color: 'bg-yellow-500',
      hoverColor: 'hover:bg-yellow-600'
    }
  ]

  const handlePayment = async () => {
    if (!selectedMethod) {
      toast.error('Veuillez sélectionner un moyen de paiement')
      return
    }

    if (!phoneNumber || phoneNumber.length < 9) {
      toast.error('Veuillez entrer un numéro de téléphone valide')
      return
    }

    setIsProcessing(true)

    try {
      // Simuler un appel API de paiement
      await new Promise(resolve => setTimeout(resolve, 2000))

      // TODO: Intégrer l'API réelle de paiement ici
      const paymentData = {
        method: selectedMethod,
        phone: phoneNumber,
        amount: totalPrice,
        duration: duration,
        imageCount: imageCount,
        postId: postId,
        timestamp: new Date().toISOString()
      }

      console.log('Payment data:', paymentData)

      // Mettre à jour le post avec la durée choisie
      if (postId) {
        try {
          await apiClient.put(`/posts/${postId}`, { duration: duration, payment_status: 'paid' })
        } catch (err) {
          console.error('Post update error:', err)
        }
      }

      // Simuler un paiement réussi
      toast.success('Paiement effectué avec succès !')
      
      // Rediriger vers le dashboard après paiement réussi
      setTimeout(() => {
        navigate('/dashboard', { 
          state: { 
            paymentSuccess: true,
            message: isEdit ? 'Annonce mise à jour avec succès !' : 'Annonce publiée avec succès !' 
          } 
        })
      }, 1500)

    } catch (error) {
      console.error('Erreur de paiement:', error)
      toast.error('Erreur lors du paiement. Veuillez réessayer.')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#fefcf8] py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* En-tête */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-[#001F3F] mb-2">Paiement de la publication</h1>
            <p className="text-gray-600">
              {isEdit ? 'Modifiez votre annonce' : 'Publiez votre annonce'} {isFreeTrialActive ? 'gratuitement' : `pour ${totalPrice} FCFA`}
            </p>
          </div>

          {/* Période d'essai gratuite */}

          {isFreeTrialActive && (
            <div className="mb-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-800 mb-2">🎉 Période d'essai gratuite active !</h3>
                  <p className="text-green-700 mb-3">
                    Vous bénéficiez de <span className="font-bold">{freeTrialDaysLeft} jours</span> d'utilisation gratuite. 
                    Profitez-en pour publier vos annonces sans frais !
                  </p>
                  <button
                    onClick={handleFreeTrial}
                    disabled={isProcessing}
                    className="w-full px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors shadow-lg disabled:opacity-50"
                  >
                    {isProcessing ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Publication...
                      </span>
                    ) : (
                      '✨ Publier gratuitement'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Résumé */}

          {!isFreeTrialActive && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-[#001F3F] mb-4">Résumé de votre annonce</h3>
              
              {/* Sélecteur de durée */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Durée de publication
                  {(postType === 'video' || postType === 'banner') && (
                    <span className="ml-2 text-xs bg-[#D4AF37] text-white px-2 py-1 rounded">
                      Tarif Premium
                    </span>
                  )}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[3, 7, 14, 30].map((days) => {
                    const pricing = getPricing()
                    const price = pricing[days]
                    const savings = days === 14 ? (postType === 'video' || postType === 'banner' ? '-8%' : '-10%') : days === 30 ? (postType === 'video' || postType === 'banner' ? '-17%' : '-20%') : null
                    return (
                      <button
                        key={days}
                        type="button"
                        onClick={() => setDuration(days)}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          duration === days
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 shadow-md scale-105'
                            : 'border-gray-200 hover:border-[#D4AF37]/50'
                        }`}
                      >
                        <div className="text-xl font-bold text-[#001F3F]">{days}</div>
                        <div className="text-xs text-gray-600 mb-1">jours</div>
                        <div className="text-sm font-semibold text-[#D4AF37]">{price} FCFA</div>
                        {days === 7 && <div className="text-xs text-green-600 mt-1">⭐ Top</div>}
                        {savings && <div className="text-xs text-blue-600 mt-1">{savings}</div>}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Détails tarifaires */}

              <div className="space-y-3 text-sm border-t pt-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Type d'opération:</span>
                  <span className="font-medium">{isEdit ? 'Modification' : 'Publication'}</span>
                </div>
                {(postType === 'banner' || postType === 'video') && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type de contenu:</span>
                    <span className="font-medium">{postType === 'banner' ? 'Bannière' : 'Vidéo'}</span>
                  </div>
                )}
                <div className="flex justify-between bg-[#D4AF37]/10 p-2 rounded">
                  <span className="text-gray-700 font-medium">📅 Durée d'affichage:</span>
                  <span className="font-bold text-[#D4AF37]">{duration} jours</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Prix de la durée ({duration} jours):</span>
                  <span className="font-medium">{getPricing()[duration]} FCFA</span>
                </div>
                {postType !== 'video' && postType !== 'banner' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Nombre d'images:</span>
                      <span className="font-medium">{imageCount} image{imageCount > 1 ? 's' : ''}</span>
                    </div>
                    {imageCount > 1 && (
                      <div className="flex justify-between text-blue-600">
                        <span>Images supplémentaires (+{imageCount - 1}):</span>
                        <span className="font-medium">+{(imageCount - 1) * PRICE_PER_IMAGE} FCFA</span>
                      </div>
                    )}
                  </>
                )}
                <div className="flex justify-between pt-3 border-t-2 border-dashed">
                  <span className="font-bold text-[#001F3F]">Montant total:</span>
                  <span className="font-bold text-[#D4AF37] text-lg">{totalPrice} FCFA</span>
                </div>
              </div>

              {/* Explication de la tarification */}

              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-xs text-blue-700">
                    {postType === 'video' || postType === 'banner' ? (
                      <>💡 Tarif Premium pour vidéos et bannières. Plus grande visibilité garantie ! Économisez jusqu'à 17% avec l'offre 30 jours.</>
                    ) : (
                      <>💡 Plus la durée est longue, plus vous économisez ! Tarifs dégressifs : 14 jours (-10%), 30 jours (-20%). {PRICE_PER_IMAGE} FCFA/image supplémentaire.</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Sélection du moyen de paiement */}

          {!isFreeTrialActive && (
            <div className="mb-8">
            <label className="block text-lg font-semibold text-[#001F3F] mb-4">
              Choisissez votre moyen de paiement
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {paymentMethods.map((method) => (
                <button
                  key={method.id}
                  onClick={() => setSelectedMethod(method.id)}
                  className={`relative p-6 rounded-xl border-2 transition-all ${
                    selectedMethod === method.id
                      ? 'border-[#D4AF37] bg-[#D4AF37]/5 shadow-lg'
                      : 'border-gray-200 hover:border-[#D4AF37]/50'
                  }`}
                >
                  {selectedMethod === method.id && (
                    <div className="absolute top-2 right-2">
                      <svg className="w-6 h-6 text-[#D4AF37]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="text-4xl mb-3">{method.icon}</div>
                  <div className="font-semibold text-[#001F3F]">{method.name}</div>
                </button>
              ))}
            </div>
          </div>
          )}

          {/* Numéro de téléphone */}

          {!isFreeTrialActive && selectedMethod && (
            <div className="mb-8 animate-fadeIn">
              <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                Numéro de téléphone
              </label>
              <div className="relative">
                <span className="absolute left-4 top-3.5 text-gray-500">+221</span>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                  placeholder="77 123 45 67"
                  maxLength="9"
                  className="w-full pl-16 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Vous recevrez une notification {selectedMethod === 'wave' ? 'Wave' : selectedMethod === 'orange' ? 'Orange Money' : 'MTN Money'} pour confirmer le paiement
              </p>
            </div>
          )}

          {/* Boutons d'action */}

          {!isFreeTrialActive && (
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={isProcessing}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
            >
              Retour
            </button>
            <button
              onClick={handlePayment}
              disabled={!selectedMethod || !phoneNumber || isProcessing}
              className="flex-1 px-6 py-3 bg-[#D4AF37] text-white font-semibold rounded-lg hover:bg-[#c09d2a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {isProcessing ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Traitement...
                </span>
              ) : (
                `Payer ${totalPrice} FCFA`
              )}
            </button>
          </div>
          )}

          {/* Bouton retour pour période gratuite */}

          {isFreeTrialActive && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={isProcessing}
              className="w-full mt-4 px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
            >
              Retour
            </button>
          )}

          {/* Note de sécurité */}

          {!isFreeTrialActive && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Paiement sécurisé</p>
                <p className="text-blue-600">Vos informations de paiement sont protégées et sécurisées.</p>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PaymentPage
